import React, { FC } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Variant as VariantType } from 'shared/types/product';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Typography from 'components/commercetools-ui/atoms/typography';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { extractAttributeName } from 'helpers/stringHelpers/extractAttributeName';
import { textToColor } from 'helpers/textToColor/textToColor';
import { classnames } from 'helpers/utils/classnames';
import VariantSelector, { isPlainLeftClick } from './variant-selector';
import { shouldUseDropdown, sortAxisValues } from '../helpers/optionAxes';

type ProductAttributeProps = {
  className?: string;
  currentVariant: VariantType;
  attribute: string;
  attributeLabel?: string; // Human-readable label from the attribute definition
  isColorAttribute?: boolean;
  inModalVersion?: boolean;
  selectedOption?: string; // Optional value that overrides the selected option calculated based on the current variant chosen.
  options: string[];
  onClick?: (attribute: string, value: string) => void;
  /** Variant page for an option (see VariantSelector.getOptionHref). */
  getOptionHref?: (option: string) => string | undefined;
  /** Full variant name for an option's link (see VariantSelector.getOptionLinkLabel). */
  getOptionLinkLabel?: (option: string) => string | undefined;
  optionLabels?: Record<string, string>;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Set when the caller already renders a step header for this attribute, so the label isn't doubled. */
  hideLabel?: boolean;
};

const ColorBackground = ({ backgroundColor }: { backgroundColor: string }) => (
  <div className="size-32 border border-[#d7d7d7]" style={{ backgroundColor }} />
);

const DropdownButton = ({
  selectedOption,
  isColorAttribute,
  backgroundColor,
  label,
  ...props
}: {
  selectedOption: string;
  isColorAttribute: boolean;
  backgroundColor?: string;
  label?: string;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    className={classnames('flex w-full cursor-pointer items-center justify-between bg-white', props.className)}
  >
    <div className="flex items-center gap-8">
      {isColorAttribute && backgroundColor && <ColorBackground backgroundColor={backgroundColor} />}
      <Typography className="body-text-small text-14 font-medium text-black">{label ?? selectedOption}</Typography>
    </div>
    <ChevronDownIcon strokeWidth={2} className="w-16 text-secondary-black" />
  </div>
);

const ProductAttribute: FC<ProductAttributeProps> = ({
  className,
  currentVariant,
  options,
  selectedOption,
  attribute,
  attributeLabel,
  isColorAttribute = false,
  inModalVersion,
  onClick,
  getOptionHref,
  getOptionLinkLabel,
  optionLabels,
  isOpen,
  onOpenChange,
  hideLabel = false,
}) => {
  const { formatMessage } = useFormat({ name: 'product' });
  const attributeString = attribute.toString();
  // Same ordering the button path uses, so the dropdown can't disagree with it.
  const sortedOptions = sortAxisValues(attribute, options);
  const optionCount = sortedOptions.length;
  const isTextAttribute = !isColorAttribute;

  const finalSelectedOption: string = selectedOption ?? currentVariant.attributes?.[attribute];

  const labelClassName = useClassNames([
    'capitalize font-medium leading-loose',
    inModalVersion ? 'text-12' : 'text-16',
  ]);
  const variantContainerClassName = useClassNames(['mt-12 flex', 'gap-12']);

  /**
   * =====================================================
   * DISPLAY RULES
   * =====================================================
   *
   * Buttons unless the axis is a long, dimension-like list — see shouldUseDropdown, which is
   * shared with VariantSelector so both agree. Identity choices (species, material, colour,
   * tread width/returns) stay buttons at any count, because a shopper picks those by scanning
   * them; only genuinely long dimension lists collapse.
   *
   * Screen size is deliberately NOT part of this: collapsing every option list on mobile hid
   * choices like Species behind a select.
   */
  const useDropdown = isTextAttribute && shouldUseDropdown(attribute, optionCount);

  const showVariantSelector = isColorAttribute || !useDropdown;

  const showDropdown = useDropdown;

  return (
    <div className={className}>
      {/* ATTRIBUTE LABEL */}
      {!hideLabel && (
        <Typography className={labelClassName}>
          {attributeLabel ||
            formatMessage({ id: attributeString, defaultMessage: extractAttributeName(attributeString) })}
        </Typography>
      )}

      {/* BUTTONS / TILES */}
      {showVariantSelector && (
        <VariantSelector
          attribute={attribute}
          options={options}
          selectedOption={finalSelectedOption}
          isVisual={isColorAttribute}
          onSelect={(value) => onClick?.(attribute, String(value))}
          optionLabels={optionLabels}
          getOptionHref={getOptionHref}
          getOptionLinkLabel={getOptionLinkLabel}
        />
      )}

      {/* DROPDOWN */}
      {showDropdown && (
        <div className={variantContainerClassName}>
          <Dropdown
            className="py-12"
            // Kept in the DOM while closed so every option's variant link is in the page HTML.
            keepMenuMounted
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            customButtonElement={
              <DropdownButton
                aria-haspopup="menu"
                isColorAttribute={isColorAttribute}
                selectedOption={isColorAttribute ? textToColor(finalSelectedOption).label : finalSelectedOption}
                backgroundColor={isColorAttribute ? textToColor(finalSelectedOption).code : undefined}
                label={optionLabels?.[finalSelectedOption]}
              />
            }
          >
            <div role="menu">
              {sortedOptions.map((option) => {
                const href = getOptionHref?.(String(option));
                const fullName = href ? getOptionLinkLabel?.(String(option)) : undefined;
                const Item = href ? 'a' : 'div';
                // Same link semantics as VariantSelector: a plain click or Space selects in place,
                // a modified click opens the variant page, and the selection is aria-current.
                return (
                  <Item
                    key={`${currentVariant.id}-${currentVariant.sku}-${attribute}-${option}`}
                    role="menuitem"
                    {...(href ? { href } : {})}
                    aria-current={String(option) === String(finalSelectedOption) ? 'true' : undefined}
                    className="block w-full cursor-pointer bg-white hover:bg-neutral-200 active:bg-neutral-200"
                    onClick={(event: React.MouseEvent) => {
                      if (href && !isPlainLeftClick(event)) return;
                      event.preventDefault();
                      onClick?.(attribute, String(option));
                    }}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (!href || event.key !== ' ') return;
                      event.preventDefault();
                      onClick?.(attribute, String(option));
                    }}
                  >
                    <div className="flex w-full items-center justify-between px-16 py-12">
                      <div className="flex items-center gap-8">
                        {isColorAttribute && <ColorBackground backgroundColor={textToColor(option).code} />}
                        <Typography className="text-14 font-normal text-primary-black">
                          {isColorAttribute ? textToColor(option).label : (optionLabels?.[option] ?? String(option))}
                        </Typography>
                        {/* The visible label leads the link's name; the full variant name follows for crawlers. */}
                        {fullName && <span className="sr-only"> – {fullName}</span>}
                      </div>
                    </div>
                  </Item>
                );
              })}
            </div>
          </Dropdown>
        </div>
      )}
    </div>
  );
};

export default ProductAttribute;
